import { CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

const SECTION_SELECTOR = 'cart-items-component[data-section-id]';

function getSectionId(target) {
  return target.closest(SECTION_SELECTOR)?.dataset.sectionId;
}

async function updateCart(target, endpoint, body) {
  const sectionId = getSectionId(target);
  if (!sectionId) return;

  const payload = {
    ...body,
    sections: sectionId,
    sections_url: window.location.pathname,
  };

  target.closest(SECTION_SELECTOR)?.classList.add('cart-items-disabled');

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok || data.errors) {
    throw new Error(data.description || data.errors || 'Cart update failed');
  }

  if (data.sections?.[sectionId]) {
    morphSection(sectionId, data.sections[sectionId]);
  }

  if (endpoint === Theme.routes.cart_add_url) {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
    window.NolaPostHog?.trackAddToCart({
      addedItem: {
        variant_id: target.dataset.variantId,
        product_id: target.dataset.productId,
        product_title: target.dataset.productTitle,
        quantity: body.items?.[0]?.quantity || 1,
      },
      cart,
      addedFrom: 'cart_drawer',
    });
  } else {
    window.NolaPostHog?.trackBundleThresholdState(data);
  }

  document.dispatchEvent(
    new CartUpdateEvent(data, sectionId, {
      itemCount: data.item_count,
      source: 'nola-cart-drawer',
      sections: data.sections,
    })
  );
}

function initPromo(root = document) {
  root.querySelectorAll('[data-nola-cart-promo]').forEach((promo) => {
    if (!(promo instanceof HTMLElement)) return;

    const progress = Math.max(0, Math.min(100, Number(promo.dataset.progress) || 0));
    const current = Number(promo.dataset.current) || 0;
    const goal = Number(promo.dataset.goal) || 2;
    const progressBar = promo.querySelector('[role="progressbar"]');

    if (progressBar) {
      progressBar.setAttribute('aria-valuenow', String(Math.min(current, goal)));
      progressBar.setAttribute('aria-valuemax', String(goal));
    }

    if (promo.dataset.promoInit !== 'true') {
      promo.dataset.promoInit = 'true';
      promo.style.setProperty('--nola-cart-promo-progress', '0%');
      requestAnimationFrame(() => {
        promo.style.setProperty('--nola-cart-promo-progress', `${progress}%`);
      });
      return;
    }

    promo.style.setProperty('--nola-cart-promo-progress', `${progress}%`);
  });
}

document.addEventListener('click', async (event) => {
  const quantityButton = event.target.closest('[data-nola-cart-qty]');
  const removeButton = event.target.closest('[data-nola-cart-remove]');
  const giftButton = event.target.closest('[data-nola-gift-toggle]');
  const quickAddButton = event.target.closest('[data-nola-cart-quick-add]');
  const action = quantityButton || removeButton || giftButton || quickAddButton;

  if (!action) return;

  event.preventDefault();
  action.disabled = true;

  try {
    if (quantityButton) {
      await updateCart(quantityButton, Theme.routes.cart_change_url, {
        line: Number(quantityButton.dataset.line),
        quantity: Math.max(0, Number(quantityButton.dataset.quantity)),
      });
    } else if (removeButton) {
      await updateCart(removeButton, Theme.routes.cart_change_url, {
        line: Number(removeButton.dataset.line),
        quantity: 0,
      });
    } else if (giftButton) {
      const isChecked = giftButton.getAttribute('aria-checked') === 'true';
      if (isChecked && Number(giftButton.dataset.line) > 0) {
        await updateCart(giftButton, Theme.routes.cart_change_url, {
          line: Number(giftButton.dataset.line),
          quantity: 0,
        });
      } else {
        await updateCart(giftButton, Theme.routes.cart_add_url, {
          items: [{ id: Number(giftButton.dataset.variantId), quantity: 1 }],
        });
      }
    } else if (quickAddButton) {
      await updateCart(quickAddButton, Theme.routes.cart_add_url, {
        items: [{ id: Number(quickAddButton.dataset.variantId), quantity: 1 }],
      });
      quickAddButton.classList.add('is-added');
      setTimeout(() => quickAddButton.classList.remove('is-added'), 1200);
    }
  } catch (error) {
    console.error('[nola-cart]', error);
    action.disabled = false;
  }
});

document.addEventListener('DOMContentLoaded', () => initPromo());
document.addEventListener('cart:update', () => requestAnimationFrame(() => initPromo()));
document.addEventListener('shopify:section:load', (event) => initPromo(event.target));
